import { Static, Type } from '@sinclair/typebox';
import { AdminCrmSubjectSchema, CrmContactStatus, CrmLifecycleStage } from './crm-contact';
import { CrmBlockedStep, CrmQueueEntryStatus } from './crm-queue';
import { NonConversionReason } from './non-conversion';

// =====================================================================
// CRM — retrouver et filtrer (lot 4) : recherche unique par personne, liste « Personnes » avec
// filtres cumulables (combinés par ET, compte toujours visible), quatre vues prédéfinies.
// =====================================================================

export enum CrmPeopleCallsFilter {
  NONE = 'none',
  SOME = 'some',
}

export enum CrmPeopleTrainingFilter {
  ENROLLED = 'enrolled',
  ATTENDED = 'attended',
  MISSED = 'missed',
  NEVER = 'never',
}

/** Démo (séance de présentation du compte Propelsia) : pointée présente, absente, ou jamais inscrite. */
export enum CrmPeopleDemoFilter {
  ATTENDED = 'attended',
  MISSED = 'missed',
  NEVER = 'never',
}

export enum CrmPeopleCallbackFilter {
  THIS_WEEK = 'this_week',
  OVERDUE = 'overdue',
}

export enum CrmPeopleSort {
  RECENT = 'recent',
  TRIAL_ENDED = 'trial_ended',
  LAST_CALL = 'last_call',
  NAME = 'name',
}

/** Clés de filtre, toutes facultatives, combinées par ET. Un filtre multivalué est une liste séparée par des virgules. */
export const AdminCrmPeopleFiltersSchema = Type.Object({
  q: Type.Optional(Type.String({ maxLength: 120 })),
  stage: Type.Optional(Type.String()),
  calls: Type.Optional(Type.Enum(CrmPeopleCallsFilter)),
  contact_allowed: Type.Optional(Type.Boolean()),
  callback: Type.Optional(Type.Enum(CrmPeopleCallbackFilter)),
  blocked_step: Type.Optional(Type.String()),
  reason: Type.Optional(Type.String()),
  training: Type.Optional(Type.Enum(CrmPeopleTrainingFilter)),
  demo: Type.Optional(Type.Enum(CrmPeopleDemoFilter)),
  has_phone: Type.Optional(Type.Boolean()),
  has_email: Type.Optional(Type.Boolean()),
  score_min: Type.Optional(Type.Number()),
  score_max: Type.Optional(Type.Number()),
  trial_ended_days_max: Type.Optional(Type.Number()),
  signed_up_days_max: Type.Optional(Type.Number()),
  city: Type.Optional(Type.String({ maxLength: 80 })),
  in_queue: Type.Optional(Type.Boolean()),
  migrated: Type.Optional(Type.Boolean()),
  sort: Type.Optional(Type.Enum(CrmPeopleSort)),
  page: Type.Optional(Type.Number()),
  per_page: Type.Optional(Type.Number()),
});
export type AdminCrmPeopleFilters = Static<typeof AdminCrmPeopleFiltersSchema>;

export const AdminCrmPersonItemSchema = Type.Object({
  subject: AdminCrmSubjectSchema,
  stage: Type.Enum(CrmLifecycleStage),
  city: Type.Optional(Type.String()),
  phone_e164: Type.Optional(Type.String()),
  email: Type.Optional(Type.String()),
  contact_status: Type.Enum(CrmContactStatus),
  signed_up_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'signed_up_at' } })),
  trial_end_at_formatted: Type.Optional(Type.String({ format: 'date-time', meta: { source_key: 'trial_end_at' } })),
  config_score: Type.Optional(Type.Number()),
  calls_count: Type.Number(),
  last_call_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'last_call_at', with_time: true } }),
  ),
  queue_status: Type.Optional(Type.Enum(CrmQueueEntryStatus)),
  next_call_at_formatted: Type.Optional(
    Type.String({ format: 'date-time', meta: { source_key: 'next_call_at', with_time: true } }),
  ),
  non_conversion_reason: Type.Optional(Type.Enum(NonConversionReason)),
  blocked_step: Type.Optional(Type.Enum(CrmBlockedStep)),
  training_attended: Type.Boolean(),
  training_missed: Type.Boolean(),
  is_migrated: Type.Boolean(),
});
export type AdminCrmPersonItem = Static<typeof AdminCrmPersonItemSchema>;

export const AdminCrmPeopleResponseSchema = Type.Object({
  items: Type.Array(AdminCrmPersonItemSchema),
  total: Type.Number(),
  page: Type.Number(),
  per_page: Type.Number(),
});
export type AdminCrmPeopleResponse = Static<typeof AdminCrmPeopleResponseSchema>;

export const AdminCrmSearchResponseSchema = Type.Object({
  items: Type.Array(AdminCrmPersonItemSchema),
});
export type AdminCrmSearchResponse = Static<typeof AdminCrmSearchResponseSchema>;

/** Vues prédéfinies (décision D12 : dans le code, l'enregistrement libre arrive avec les audiences). */
export type CrmPeopleView = {
  key: string;
  /** Clé de libellé dans le namespace ADMIN. */
  label_key: string;
  filters: AdminCrmPeopleFilters;
};

export const CRM_PEOPLE_VIEWS: readonly CrmPeopleView[] = [
  {
    key: 'trial_ended_never_called',
    label_key: 'CRM_PEOPLE_VIEW_TRIAL_ENDED_NEVER_CALLED',
    filters: { stage: CrmLifecycleStage.TRIAL_ENDED, calls: CrmPeopleCallsFilter.NONE, contact_allowed: true },
  },
  {
    key: 'callbacks_this_week',
    label_key: 'CRM_PEOPLE_VIEW_CALLBACKS_THIS_WEEK',
    filters: { callback: CrmPeopleCallbackFilter.THIS_WEEK, contact_allowed: true },
  },
  {
    key: 'blocked_on_catalog',
    label_key: 'CRM_PEOPLE_VIEW_BLOCKED_ON_CATALOG',
    filters: { blocked_step: CrmBlockedStep.CATALOG },
  },
  {
    key: 'trained_not_subscribed',
    label_key: 'CRM_PEOPLE_VIEW_TRAINED_NOT_SUBSCRIBED',
    filters: {
      training: CrmPeopleTrainingFilter.ATTENDED,
      stage: [CrmLifecycleStage.TRIAL, CrmLifecycleStage.TRIAL_ENDED, CrmLifecycleStage.LEAD].join(','),
    },
  },
];

/** Longueur maximale d'une question posée à Pulse sur un dossier. */
export const CRM_DOSSIER_QUESTION_MAX_LENGTH = 500;

export const AdminCrmDossierSummaryBodySchema = Type.Object({
  lead_id: Type.Optional(Type.Number()),
  professional_id: Type.Optional(Type.Number()),
  /** Question directe sur le dossier. Absente : Pulse rédige le résumé en trois lignes. */
  question: Type.Optional(Type.String({ maxLength: CRM_DOSSIER_QUESTION_MAX_LENGTH })),
});
export type AdminCrmDossierSummaryBody = Static<typeof AdminCrmDossierSummaryBodySchema>;

/** Résumé de dossier rédigé par Pulse (trois lignes), jamais écrit sur la fiche. */
export const AdminCrmDossierSummaryResponseSchema = Type.Object({
  summary: Type.String(),
  generated_at_formatted: Type.String({
    format: 'date-time',
    meta: { source_key: 'generated_at', with_time: true },
  }),
});
export type AdminCrmDossierSummaryResponse = Static<typeof AdminCrmDossierSummaryResponseSchema>;
